import React from "react";
import PropTypes from "prop-types";
import "./Flex.css";

/**
 * Composant `Flex` pour une disposition en flexbox simplifiée avec des types prédéfinis.
 *
 * @param {string} type - Nom du type prédéfini (par ex. "center", "space-center", "column-start").
 * @param {React.ReactNode} children - Éléments enfants affichés dans le conteneur flex.
 * @param {string} className - Classes CSS supplémentaires.
 * @param {number} gap - Espacement entre les éléments enfants (en pixels).
 * @param {object} style - Styles CSS additionnels à appliquer.
 * @param {boolean} wrap - Indique si les éléments enfants doivent être enroulés sur plusieurs
 * @param {object} props - Autres attributs à passer au composant.
 * @returns {JSX.Element} Un conteneur flex configuré dynamiquement.
 */
const Flex = ({
  type = "start-center",
  children,
  className = "",
  gap = 0,
  style = {},
  wrap = false,
  ...props
}) => {
  // Mapping des types prédéfinis à des styles flexbox
  const typeMap = {
    row: { display: "flex" },
    center: { justifyContent: "center", alignItems: "center" },
    "between-center": {
      justifyContent: "space-between",
      alignItems: "center",
    },
    col: {
      flexDirection: "column",
    },
    "col-center": {
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    "col-between": {
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "center",
    },
    "col-start": {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    "col-start-reverse": {
      flexDirection: "column-reverse",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    "col-end": {
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    "start-center": {
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    "end-center": {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    "-end": {
      flexDirection: "row",
      alignItems: "end",
    },
    "around-center": {
      justifyContent: "space-around",
      alignItems: "center",
    },
    "evenly-center": {
      justifyContent: "space-evenly",
      alignItems: "center",
    },
    "center-start": {
      justifyContent: "center",
      alignItems: "flex-start",
    },
  };

  // Utilisation du style basé sur le type ou un fallback par défaut
  const flexStyle = typeMap[type] || typeMap.center;

  return (
    <div
      className={`Flex ${className}`}
      style={{
        ...flexStyle,
        ...style,
        gap: `${gap}px`,
        flexWrap: wrap ? "wrap" : "nowrap",
      }}
      {...props}
    >
      {children}
    </div>
  );
};

Flex.propTypes = {
  type: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  gap: PropTypes.number,
  style: PropTypes.object,
  wrap: PropTypes.bool,
  props: PropTypes.object,
};

export default Flex;
